import { IonIcon, useIonToast } from "@ionic/react";
import { documentTextOutline, receiptOutline } from "ionicons/icons";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { purchasedItinerariesSlice, usePersistedSliceSelector } from "../../store/persistence";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { openCreditNotePdf, openInvoicePdf } from "../../tools/invoiceDownload.tools";
import { type CreditNoteSummaryDTO } from "../../types/dtos/api/purchase/credit-note-summary.dto";
import PriceConversion from "../ItinerariesPlanner/pricing/PriceConversion";
import { resolveUiSpring } from "../shared/motion/uiMotion.tools";
import { SheetModal } from "../shared/SheetModal";
import { TicketRefundPanel } from "./TicketRefundPanel";
import { TicketViewerDeck } from "./TicketViewerDeck";
import { buildTicketViewerItems, resolveInitialSlideIndex, type TicketViewerItem } from "./ticketViewer.tools";
import { useTicketBrightness } from "./useTicketBrightness.hook";

/**
 * Props of {@link TicketViewerModal}.
 */
export interface TicketViewerModalProps {
    "isOpen":   boolean;
    "onClose":  () => void;
    "isDarkUi": boolean;

    /** `entry.itinerary.id` of the purchased itinerary — `null` while closed. */
    "itineraryId": string | null;

    /** `offer.segments` key to open on — `null` opens the first ticket. */
    "initialSegmentId": string | null;
}

/** Everything the deck needs — kept after close so the exit slide-down keeps its content. */
interface TicketViewerPayload {
    "items":            readonly TicketViewerItem[];
    "initialIndex":     number;
    "initialSegmentId": string | null;
    "itineraryId":      string;
    "provider":         string;

    /** FlowRide booking row id — invoice/credit-note download key; `null` on entries synced before invoices. */
    "bookingId": string | null;

    /** Invoice number (`FW-YYYY-NNNNNN`) for the downloaded file name. */
    "invoiceNumber": string | null;

    /** Credit notes issued against the booking's invoice (may be empty). */
    "creditNotes": CreditNoteSummaryDTO[];
}

/**
 * Purchased-tickets viewer: stacked ticket cards of one purchased itinerary.
 *
 * @remarks Fully independent from the purchase wizard — it only shares the
 * `SheetModal` shell. Data comes from the persisted `purchasedItineraries`
 * slice, so tickets stay available offline and refresh with the bookings sync.
 * @param props - Component props.
 * @returns The viewer, or `null` when closed or without tickets.
 */
export const TicketViewerModal: React.FC<TicketViewerModalProps> = ( {
    isOpen,
    onClose,
    isDarkUi,
    itineraryId,
    initialSegmentId,
} ) => {
    const entry = usePersistedSliceSelector(
        purchasedItinerariesSlice,
        ( entries ) => ( itineraryId === null ? undefined : entries.find( ( item ) => item.itinerary.id === itineraryId ) ),
    );
    const items = useMemo( () => ( entry === undefined ? [] : buildTicketViewerItems( entry ) ), [ entry ] );
    const initialIndex = useMemo( () => resolveInitialSlideIndex( items, initialSegmentId ), [ items, initialSegmentId ] );
    const [ flippedIndex, setFlippedIndex ] = useState<number | null>( null );
    const [ refundTargetTicketId, setRefundTargetTicketId ] = useState<string | null>( null );
    const [ documentsOpen, setDocumentsOpen ] = useState( false );
    const documentsAnchorRef = useRef<HTMLDivElement | null>( null );
    const [ presentToast ] = useIonToast();
    const reducedMotion = useReducedMotion();
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    const livePayload = useMemo<TicketViewerPayload | null>(
        () => ( isOpen && entry !== undefined && items.length > 0 && itineraryId !== null
            ? {
                items,
                initialIndex,
                initialSegmentId,
                itineraryId,
                "provider":      entry.purchase.provider,
                "bookingId":     entry.purchase.bookingId ?? null,
                "invoiceNumber": entry.purchase.invoiceNumber ?? null,
                "creditNotes":   entry.purchase.creditNotes ?? [],
            }
            : null ),
        [ isOpen, entry, items, initialIndex, initialSegmentId, itineraryId ],
    );

    /**
     * Downloads and opens the invoice PDF (new tab on web, share sheet on native).
     * Hidden while the entry carries no `bookingId` — the next `/bookings` sync
     * fills it for entries persisted before invoices existed.
     */
    const openInvoice = useCallback( (): void => {
        const bookingId = livePayload?.bookingId;
        if ( bookingId === null || bookingId === undefined ) {
            return;
        }

        void openInvoicePdf( bookingId, livePayload?.invoiceNumber ).then( ( opened ) => {
            if ( !opened ) {
                void presentToast( {
                    "duration": 2500,
                    "message":  "La facture n'est pas encore disponible. Réessayez dans un instant.",
                    "position": "bottom",
                } );
            }
        } );
    }, [ livePayload, presentToast ] );

    /**
     * Downloads and opens a credit note PDF — the separate accounting document
     * of a refund.
     *
     * @param creditNote - Credit note summary (`id` for the download, `creditNoteNumber` for the file name).
     */
    const openCreditNote = useCallback( ( creditNote: CreditNoteSummaryDTO ): void => {
        const bookingId = livePayload?.bookingId;
        if ( bookingId === null || bookingId === undefined ) {
            return;
        }

        void openCreditNotePdf( bookingId, creditNote.id, creditNote.creditNoteNumber ).then( ( opened ) => {
            if ( !opened ) {
                void presentToast( {
                    "duration": 2500,
                    "message":  "La note de crédit n'est pas encore disponible. Réessayez dans un instant.",
                    "position": "bottom",
                } );
            }
        } );
    }, [ livePayload, presentToast ] );

    /**
     * Toggles the documents dropdown when credit notes exist — otherwise the
     * invoice is the only document and downloads directly.
     */
    const openDocuments = useCallback( (): void => {
        if ( ( livePayload?.creditNotes.length ?? 0 ) === 0 ) {
            openInvoice();

            return;
        }

        setDocumentsOpen( ( open ) => !open );
    }, [ livePayload, openInvoice ] );

    // Close the documents dropdown on any pointer down outside its anchor.
    useEffect( () => {
        if ( !documentsOpen ) {
            return;
        }

        const handlePointerDown = ( event: PointerEvent ): void => {
            if ( documentsAnchorRef.current !== null && event.target instanceof Node && !documentsAnchorRef.current.contains( event.target ) ) {
                setDocumentsOpen( false );
            }
        };

        document.addEventListener( "pointerdown", handlePointerDown );

        return () => document.removeEventListener( "pointerdown", handlePointerDown );
    }, [ documentsOpen ] );

    useTicketBrightness( isOpen && items.length > 0 );

    /*
     * Close also unflips any card — `AnimatePresence` in `SheetModal` replays
     * the frozen subtree during the slide-down, so the deck needs no data
     * cache: this component only has to stay mounted while closed.
     */
    const handleClose = useCallback( (): void => {
        setFlippedIndex( null );
        setRefundTargetTicketId( null );
        setDocumentsOpen( false );
        onClose();
    }, [ onClose ] );

    // Escape: documents dropdown first, refund panel, then back to the front face, then the viewer itself.
    useEffect( () => {
        if ( !isOpen ) {
            return;
        }

        const handleKeyDown = ( event: KeyboardEvent ): void => {
            if ( event.key === "Escape" ) {
                if ( documentsOpen ) {
                    setDocumentsOpen( false );

                    return;
                }

                if ( refundTargetTicketId !== null ) {
                    setRefundTargetTicketId( null );

                    return;
                }

                if ( flippedIndex !== null ) {
                    setFlippedIndex( null );

                    return;
                }

                handleClose();
            }
        };

        window.addEventListener( "keydown", handleKeyDown );

        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [ isOpen, handleClose, flippedIndex, refundTargetTicketId, documentsOpen ] );

    const isRefundViewOpen = refundTargetTicketId !== null && livePayload?.bookingId != null;
    const hasCreditNotes = ( livePayload?.creditNotes.length ?? 0 ) > 0;

    return (
        <SheetModal
            headerLeading={livePayload?.bookingId === null || livePayload?.bookingId === undefined || isRefundViewOpen
                ? null
                : (
                    <div
                        className="relative"
                        ref={documentsAnchorRef}
                    >
                        <button
                            aria-expanded={hasCreditNotes ? documentsOpen : undefined}
                            aria-haspopup={hasCreditNotes ? "menu" : undefined}
                            aria-label={hasCreditNotes ? "Voir les documents comptables" : "Voir la facture"}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-solid p-0 backdrop-blur-sm transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                            data-testid="ticket-viewer-invoice"
                            onClick={openDocuments}
                            style={{
                                "borderColor": palette.overlayBorderStrong,
                                "background":  isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft,
                                "color":       palette.overlayTextSecondary,
                            }}
                            type="button"
                        >
                            <IonIcon
                                aria-hidden="true"
                                icon={hasCreditNotes ? documentTextOutline : receiptOutline}
                                style={{ "fontSize": "15px" }}
                            />
                        </button>
                        <AnimatePresence>
                            {documentsOpen
                                ? (
                                    <motion.div
                                        animate={{
 "opacity": 1,
"scale":   1,
"y":       0,
}}
                                        className="absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border shadow-xl backdrop-blur-md"
                                        exit={{
 "opacity": 0,
"scale":   0.94,
"y":       -4,
}}
                                        initial={{
 "opacity": 0,
"scale":   0.92,
"y":       -6,
}}
                                        role="menu"
                                        style={{
                                            "backgroundColor": palette.overlayCardBackgroundStrong,
                                            "borderColor":     palette.overlayBorder,
                                            "transformOrigin": "top left",
                                        }}
                                        transition={resolveUiSpring( reducedMotion )}
                                    >
                                        <p
                                            className="px-3 pb-1 pt-2.5 text-xs font-bold uppercase tracking-wide"
                                            style={{ "color": palette.overlayTextSecondary }}
                                        >
                                            Documents comptables
                                        </p>
                                        <button
                                            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2"
                                            data-testid="ticket-document-invoice"
                                            onClick={() => {
                                                setDocumentsOpen( false );
                                                openInvoice();
                                            }}
                                            role="menuitem"
                                            style={{ "color": palette.overlayTextPrimary }}
                                            type="button"
                                        >
                                            <span className="min-w-0 truncate">
                                                Facture
{" "}
                                                {livePayload?.invoiceNumber ?? ""}
                                            </span>
                                            <IonIcon
                                                aria-hidden="true"
                                                className="shrink-0"
                                                icon={receiptOutline}
                                                style={{ "color": palette.overlayTextSecondary }}
                                            />
                                        </button>
                                        {( livePayload?.creditNotes ?? [] ).map( ( creditNote ) => (
                                            <button
                                                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2"
                                                data-testid={`ticket-document-credit-note-${ creditNote.id }`}
                                                key={creditNote.id}
                                                onClick={() => {
                                                    setDocumentsOpen( false );
                                                    openCreditNote( creditNote );
                                                }}
                                                role="menuitem"
                                                style={{ "color": palette.overlayTextPrimary }}
                                                type="button"
                                            >
                                                <span className="min-w-0 truncate">
                                                    Note de crédit
{" "}
                                                    {creditNote.creditNoteNumber}
                                                </span>
                                                <span
                                                    className="shrink-0 text-xs font-bold"
                                                    style={{ "color": palette.overlayTextSecondary }}
                                                >
                                                    <PriceConversion
                                                        chooseEuro={false}
                                                        price={creditNote.amountCents / 100}
                                                    />
                                                </span>
                                            </button>
                                        ) )}
                                    </motion.div>
                                )
                                : null}
                        </AnimatePresence>
                    </div>
                )}
            headerStyle={{ "backgroundColor": isDarkUi ? undefined : "#FFFFFF" }}
            isDarkUi={isDarkUi}
            isOpen={livePayload !== null}
            onClose={handleClose}
            title={isRefundViewOpen ? "Remboursement" : "Billets"}
        >
            {livePayload === null
                ? null
                : isRefundViewOpen && livePayload.bookingId !== null
                    ? (
                        <TicketRefundPanel
                            bookingId={livePayload.bookingId}
                            initialTicketId={refundTargetTicketId}
                            isDarkUi={isDarkUi}
                            items={livePayload.items}
                            onBack={() => setRefundTargetTicketId( null )}
                            onRefundSuccess={() => {
                                setRefundTargetTicketId( null );
                                setFlippedIndex( null );
                                void presentToast( {
                                    "duration": 3000,
                                    "message":  "Votre billet a été remboursé avec succès.",
                                    "position": "bottom",
                                } );
                            }}
                        />
                    )
                    : (
                        <TicketViewerDeck
                            flippedIndex={flippedIndex}
                            initialIndex={livePayload.initialIndex}
                            initialSegmentId={livePayload.initialSegmentId}
                            isDarkUi={isDarkUi}
                            items={livePayload.items}
                            itineraryId={livePayload.itineraryId}
                            onFlipCard={setFlippedIndex}
                            onRequestRefund={livePayload.bookingId === null ? undefined : ( ticketId ) => setRefundTargetTicketId( ticketId )}
                            onUnflipCard={() => setFlippedIndex( null )}
                            provider={livePayload.provider}
                        />
                    )}
        </SheetModal>
    );
};
